import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Share2 } from "lucide-react";
import {
  formatPoolDenomination,
  type JettonMetadata,
  type ProtocolPool,
} from "@zkresistor/shared";
import {
  TokenMark,
  activeDeposits,
  formatShieldedTvl,
  sharePool,
  shortAddress,
} from "@zkresistor/client";

export function PoolAvatar({ pool, large = false }: { pool: ProtocolPool; large?: boolean }) {
  return (
    <TokenMark
      className={`asset${large ? " large" : ""}`}
      image={pool.image}
      symbol={pool.symbol}
    />
  );
}

export function PoolHero({ pool }: { pool: ProtocolPool }) {
  const deposits = activeDeposits(pool);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 1_600);
    return () => window.clearTimeout(timeout);
  }, [copied, pool.address]);

  const share = () => {
    void sharePool(pool).then((result) => {
      if (result === "copied") setCopied(true);
    }).catch(() => undefined);
  };

  return (
    <div className="pool-hero">
      <PoolAvatar pool={pool} large />
      <div className="pool-hero-copy">
        <h1>{formatPoolDenomination(pool)} {pool.symbol}</h1>
        <p>{deposits.toLocaleString("en-US")} active deposit{deposits === 1 ? "" : "s"}</p>
      </div>
      <button
        type="button"
        className="row-icon pool-share-button"
        aria-label={copied ? "Pool link copied" : "Share Pool"}
        onClick={share}
      >
        {copied ? <Check size={19} /> : <Share2 size={19} />}
      </button>
    </div>
  );
}

export function PoolStats({ pool }: { pool: ProtocolPool }) {
  return (
    <div className="pool-stats">
      <div className="stat-row">
        <span>Pool size</span>
        <strong>{formatPoolDenomination(pool)} {pool.symbol}</strong>
      </div>
      <div className="stat-row">
        <span>Shielded TVL</span>
        <strong>{formatShieldedTvl(pool)} {pool.symbol}</strong>
      </div>
      <div className="stat-row">
        <span>Privacy set</span>
        <strong>{pool.privacySet.toLocaleString("en-US")}</strong>
      </div>
      <div className="stat-row">
        <span>Pool capacity</span>
        <strong>{pool.nextIndex.toLocaleString("en-US")} / {pool.capacity.toLocaleString("en-US")}</strong>
      </div>
      <div className="stat-row">
        <span>Contract</span>
        <span className="contract-actions">
          <code>{shortAddress(pool.address)}</code>
          <button
            type="button"
            className="row-icon"
            aria-label="Copy contract address"
            onClick={() => void navigator.clipboard.writeText(pool.address)}
          >
            <Copy size={18} />
          </button>
          <a
            className="row-icon"
            href={`https://tonviewer.com/${pool.address}`}
            target="_blank"
            rel="noreferrer"
            aria-label="Open contract on Tonviewer"
          >
            <ExternalLink size={18} />
          </a>
        </span>
      </div>
    </div>
  );
}

export function JettonIdentity({ metadata }: { metadata: JettonMetadata }) {
  return (
    <div className="token-identity">
      <TokenMark className="asset" image={metadata.image} symbol={metadata.symbol} />
      <span>
        <strong>{metadata.name ?? metadata.symbol}</strong>
        <small>{metadata.symbol}</small>
      </span>
      <Check size={18} aria-label="Verified on-chain metadata" />
    </div>
  );
}
